import { useState, useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  SparklesIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChartBarIcon,
  QueueListIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import { useStation } from '../../context/StationContext';
import {
  useMemorySummaryDaily,
  useMemorySummaryLatest,
  useMemorySummaryPeriodic,
  useHippocampusKnowledge,
  useNeocortexRollup,
  useKnowledgeDocumentContent,
  type DailySummaryItem,
  type KnowledgeDocumentItem,
  type PeriodicSummaryItem,
} from '../../hooks/useMemorySummary';
import { useDeviceMemory } from '../../hooks/useDeviceMemory';
import MarkdownText from '../shared/MarkdownText';
import DailySummaryChart from '../charts/DailySummaryChart';
import EmptyState from '../shared/EmptyState';
import StatusBadge from '../shared/StatusBadge';
import { toast } from '../shared/Toast';
import { useAgentOrgContext } from './useAgentOrgContext';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MemoryGraphScopeLegend from './MemoryGraphScopeLegend';
import {
  allScopesForNode,
  SCOPE_LEVEL_COLORS,
  scopeForNodeType,
  scopesForLinkType,
  computeGraphNodeHighlight,
  GRAPH_HUB_SCOPES,
  linkInvolvementForScope,
  summaryMatchesTopic,
  isGraphNodeClickable,
  type ScopeLevel,
  type MemoryGraphFocus,
  type MemoryGraphLinkType,
  type MemoryGraphNodeType,
  type ScopeInvolvement,
  type GraphScopeHighlightInput,
} from './memoryBrainRegions';

/*  Constants  */

type GraphNode = {
  id: string;
  type: MemoryGraphNodeType;
  label: string;
  radius: number;
  color: string;
  scope: ScopeLevel;
  data?: DailySummaryItem | PeriodicSummaryItem | KnowledgeDocumentItem;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  /** Rest position for gentle float animation (hub layout). */
  anchorX?: number;
  anchorY?: number;
};

type GraphLink = d3.SimulationLinkDatum<GraphNode> & {
  type: MemoryGraphLinkType;
};

/*  Helpers  */

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function extractKeywords(md: string, topN = 5): string[] {
  const stopwords = new Set([
    'the', 'and', 'to', 'of', 'a', 'in', 'is', 'it', 'for', 'with', 'as', 'on', 'by', 'at',
    'from', 'or', 'be', 'are', 'have', 'has', 'had', 'do', 'does', 'did', 'can', 'will',
    'would', 'could', 'should', 'may', 'might', 'must', 'shall', 'that', 'this', 'these',
    'those', 'which', 'who', 'what', 'when', 'where', 'why', 'how',
  ]);

  const text = md.toLowerCase().replace(/[#*\->[\]()]/g, ' ');
  const words = text.split(/\s+/).filter((w) => w.length > 2 && !stopwords.has(w));

  const freq: Record<string, number> = {};
  words.forEach((w) => {
    freq[w] = (freq[w] || 0) + 1;
  });

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([w]) => w);
}

/** Hub (station/device center) - deliberately distinct from every brain-region color. */
const HUB_COLOR = '#22d3ee';
const HUB_GLOW = '#67e8f9';
const HUB_RADIUS = 30;

function buildGraphData(
  dailyItems: DailySummaryItem[],
  periodicItems: PeriodicSummaryItem[],
  filterOrg: string,
  focus: MemoryGraphFocus | null = null,
  knowledgeDocuments: KnowledgeDocumentItem[] = [],
): { nodes: GraphNode[]; links: GraphLink[]; centerNodeId: string | null } {
  const nodes: GraphNode[] = [];
  const links: GraphLink[] = [];
  const nodeMap = new Map<string, GraphNode>();
  const topicMap = new Map<string, Set<string>>();

  const filteredDaily = filterOrg ? dailyItems.filter((d) => d.org_id === filterOrg) : dailyItems;
  const filteredPeriodic = filterOrg
    ? periodicItems.filter((p) => p.org_id === filterOrg)
    : periodicItems;

  filteredDaily.forEach((daily, i) => {
    const nodeId = `daily-${daily.summary_date}-${daily.org_id}`;
    const scope = scopeForNodeType('daily');
    const node: GraphNode = {
      id: nodeId,
      type: 'daily',
      label: daily.summary_date,
      radius: Math.min(28, Math.max(8, (daily.source_turn_count || 5) / 2)),
      color: SCOPE_LEVEL_COLORS[scope],
      scope,
      data: daily,
    };
    nodes.push(node);
    nodeMap.set(nodeId, node);

    if (i > 0 && filteredDaily[i - 1].org_id === daily.org_id) {
      const prevId = `daily-${filteredDaily[i - 1].summary_date}-${filteredDaily[i - 1].org_id}`;
      links.push({
        source: prevId,
        target: nodeId,
        type: 'temporal',
      });
    }

    extractKeywords(daily.summary_md, 5).forEach((kw) => {
      if (!topicMap.has(kw)) topicMap.set(kw, new Set());
      topicMap.get(kw)!.add(nodeId);
    });
  });

  const topicEntries = [...topicMap.entries()]
    .filter(([, nodeIds]) => nodeIds.size >= 2)
    .sort((a, b) => b[1].size - a[1].size)
    .slice(0, GRAPH_MAX_TOPICS);

  topicEntries.forEach(([keyword, nodeIds]) => {
    if (nodeIds.size >= 2) {
      const topicId = `topic-${keyword}`;
      const scope = scopeForNodeType('topic');
      const node: GraphNode = {
        id: topicId,
        type: 'topic',
        label: keyword,
        radius: 6,
        color: SCOPE_LEVEL_COLORS[scope],
        scope,
      };
      nodes.push(node);
      nodeMap.set(topicId, node);

      nodeIds.forEach((nodeId) => {
        links.push({
          source: nodeId,
          target: topicId,
          type: 'temporal',
        });
      });
    }
  });

  if (focus?.kind === 'device') {
    const deviceNodeId = `device-${focus.deviceId}`;
    const scope = scopeForNodeType('device');
    const deviceNode: GraphNode = {
      id: deviceNodeId,
      type: 'device',
      label: focus.deviceId,
      radius: HUB_RADIUS,
      color: HUB_COLOR,
      scope,
    };
    nodes.push(deviceNode);
    nodeMap.set(deviceNodeId, deviceNode);

    filteredDaily.forEach((daily) => {
      const dailyId = `daily-${daily.summary_date}-${daily.org_id}`;
      if (nodeMap.has(dailyId)) {
        links.push({ source: dailyId, target: deviceNodeId, type: 'ownership' });
      }
    });
  } else {
    const stationIds = new Set(
      [
        ...filteredDaily.map((d) => d.station_id || 'All'),
        ...filteredPeriodic.map((p) => p.station_id || 'All'),
        ...(focus?.kind === 'station' ? [focus.stationId] : []),
      ].filter(Boolean),
    );
    stationIds.forEach((station) => {
      const stationNodeId = `station-${station}`;
      const isHub = focus?.kind === 'station' && station === focus.stationId;
      const scope = scopeForNodeType('station');
      const node: GraphNode = {
        id: stationNodeId,
        type: 'station',
        label: station,
        radius: isHub ? HUB_RADIUS : 10,
        color: isHub ? HUB_COLOR : SCOPE_LEVEL_COLORS[scope],
        scope,
      };
      nodes.push(node);
      nodeMap.set(stationNodeId, node);

      filteredDaily.forEach((daily) => {
        if ((daily.station_id || 'All') === station) {
          links.push({
            source: `daily-${daily.summary_date}-${daily.org_id}`,
            target: stationNodeId,
            type: 'ownership',
          });
        }
      });
    });
  }

  // Knowledge documents surfaced as nodes — in BOTH device and station focus.
  // Link each doc to whichever hub exists: the device hub in device focus, else
  // the doc's station hub. (Previously only ran in the station/else branch, so
  // a device memory graph never showed knowledge nodes.)
  const filteredKnowledge = filterOrg
    ? knowledgeDocuments.filter((d) => d.org_id === filterOrg)
    : knowledgeDocuments;
  filteredKnowledge.slice(0, 12).forEach((doc) => {
    const nodeId = `knowledge-${doc.id}`;
    const scope = scopeForNodeType('knowledge_document');
    const knNode: GraphNode = {
      id: nodeId,
      type: 'knowledge_document',
      label: doc.filename,
      radius: 12,
      color: SCOPE_LEVEL_COLORS[scope],
      scope,
      data: doc,
    };
    nodes.push(knNode);
    nodeMap.set(nodeId, knNode);
    const hubId =
      focus?.kind === 'device'
        ? `device-${focus.deviceId}`
        : `station-${doc.station_id || (focus?.kind === 'station' ? focus.stationId : 'All')}`;
    if (nodeMap.has(hubId)) {
      links.push({ source: nodeId, target: hubId, type: 'ownership' });
    }
  });

  filteredPeriodic.forEach((periodic) => {
    const nodeId = `periodic-${periodic.period_type}-${periodic.period_start_date}-${periodic.org_id}`;
    if (nodeMap.has(nodeId)) return;
    const scope = scopeForNodeType('periodic');
    const node: GraphNode = {
      id: nodeId,
      type: 'periodic',
      label: periodic.period_type,
      radius: 14,
      color: SCOPE_LEVEL_COLORS[scope],
      scope,
      data: periodic,
    };
    nodes.push(node);
    nodeMap.set(nodeId, node);

    const hubId =
      focus?.kind === 'device'
        ? `device-${focus.deviceId}`
        : `station-${periodic.station_id || (focus?.kind === 'station' ? focus.stationId : 'All')}`;
    if (nodeMap.has(hubId)) {
      links.push({ source: nodeId, target: hubId, type: 'temporal' });
    }

    filteredDaily.forEach((daily) => {
      const day = daily.summary_date;
      if (
        day >= periodic.period_start_date &&
        day <= periodic.period_end_date &&
        daily.org_id === periodic.org_id
      ) {
        const dailyId = `daily-${daily.summary_date}-${daily.org_id}`;
        if (nodeMap.has(dailyId)) {
          links.push({ source: dailyId, target: nodeId, type: 'temporal' });
        }
      }
    });
  });

  const centerNodeId =
    focus?.kind === 'device'
      ? `device-${focus.deviceId}`
      : focus?.kind === 'station'
        ? `station-${focus.stationId}`
        : null;

  const hubId = centerNodeId ?? [...nodeMap.keys()].find((id) => id.startsWith('station-'));
  GRAPH_HUB_SCOPES.forEach((scope) => {
    const hubNodeId = `hub-${scope}`;
    if (nodeMap.has(hubNodeId)) return;
    const hubNode: GraphNode = {
      id: hubNodeId,
      type: 'hub',
      label: scope.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      radius: 9,
      color: SCOPE_LEVEL_COLORS[scope],
      scope,
    };
    nodes.push(hubNode);
    nodeMap.set(hubNodeId, hubNode);
    if (hubId && nodeMap.has(hubId)) {
      links.push({ source: hubNodeId, target: hubId, type: 'ownership' });
    }
  });

  return { nodes, links, centerNodeId };
}

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportJSON(data: unknown, filename: string) {
  downloadBlob(JSON.stringify(data, null, 2), filename, 'application/json');
}

function exportCSV(rows: string[][], filename: string) {
  const content = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  downloadBlob(content, filename, 'text/csv');
}

function formatRelativeTime(iso: string | undefined | null): string {
  if (!iso) return '\u2014';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const now = Date.now();
  const diffSec = Math.floor((now - date.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function statusVariantForKnowledge(
  status: KnowledgeDocumentItem['status'],
): 'success' | 'warning' | 'error' | 'info' | 'muted' {
  switch (status) {
    case 'ready':
      return 'success';
    case 'processing':
    case 'uploaded':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'muted';
  }
}

/*  Stats (KPI strip)  */

function StatChip({
  icon: Icon,
  label,
  value,
  hint,
  delta,
  accent = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  hint?: string;
  delta?: { value: number; positive: boolean } | null;
  accent?: 'default' | 'accent' | 'muted';
}) {
  const accentRing =
    accent === 'accent'
      ? 'ring-1 ring-app-accent/25 border-app-accent/30'
      : accent === 'muted'
        ? 'border-app-border/60'
        : 'border-app-border/80';

  return (
    <div
      className={`relative flex min-w-0 items-center gap-3 overflow-hidden rounded-xl border bg-gradient-to-br from-app-surface to-app-muted/25 px-3 py-3 shadow-sm ${accentRing}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-app-accent/10">
        <Icon className="h-4 w-4 text-app-accent" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
          {label}
        </p>
        <p
          className="truncate text-base font-bold tabular-nums text-app-text"
          title={String(value)}
        >
          {value}
        </p>
        {hint ? <p className="truncate text-[10px] text-app-text-sec">{hint}</p> : null}
      </div>
      {delta ? (
        <span
          className={`inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            delta.positive ? 'bg-app-success/15 text-app-success' : 'bg-app-error/15 text-app-error'
          }`}
        >
          {delta.positive ? (
            <ArrowTrendingUpIcon className="h-3 w-3" />
          ) : (
            <ArrowTrendingDownIcon className="h-3 w-3" />
          )}
          {delta.positive ? '+' : ''}
          {delta.value}%
        </span>
      ) : null}
    </div>
  );
}

function Panel({
  title,
  subtitle,
  icon: Icon,
  headerAction,
  children,
  className = '',
  bodyClassName = '',
}: {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-app-border/80 bg-app-surface shadow-sm ${className}`}
    >
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-app-border/60 bg-gradient-to-r from-app-muted/40 to-transparent px-4 py-3">
        <div className="flex min-w-0 items-start gap-2.5">
          {Icon ? (
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-app-accent/10">
              <Icon className="h-5 w-5 text-app-accent" />
            </span>
          ) : null}
          <div className="min-w-0">
            <h2 className="truncate text-[16px] font-semibold text-app-text">{title}</h2>
            {subtitle ? (
              <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-app-text-dim">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </header>
      <div className={`min-h-0 flex-1 overflow-y-auto p-4 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

function MemoryTimelineRow({
  primary,
  secondary,
  turns,
  selected,
  preview,
  onClick,
}: {
  primary: string;
  secondary?: string;
  turns: number;
  selected: boolean;
  preview?: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`group flex w-full gap-3 px-3 py-3 text-left transition-all hover:bg-app-muted/35 ${
          selected
            ? 'bg-app-accent/10 ring-1 ring-inset ring-app-accent/35'
            : 'border-l-2 border-transparent hover:border-l-app-accent/40'
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold tabular-nums text-app-text">{primary}</span>
            {secondary ? (
              <span className="rounded-md bg-app-muted/50 px-1.5 py-0.5 text-[10px] capitalize text-app-text-sec">
                {secondary}
              </span>
            ) : null}
          </div>
          {preview ? (
            <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-app-text-dim">
              {preview.replace(/<!--[\s\S]*?-->/g, '').replace(/^#+\s*/gm, '').slice(0, 120)}
              {preview.length > 120 ? '…' : ''}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end justify-center gap-1">
          <span className="rounded-full bg-app-muted/60 px-2 py-0.5 text-[10px] font-medium tabular-nums text-app-text-sec">
            {turns} {t('memory.turns', { defaultValue: 'turns' })}
          </span>
          <ChevronRightIcon
            className={`h-4 w-4 transition-transform ${
              selected ? 'text-app-accent' : 'text-app-text-dim group-hover:translate-x-0.5'
            }`}
          />
        </div>
      </button>
    </li>
  );
}

function nodeTypeBadge(
  type: MemoryGraphNodeType,
  t: (key: string, opts?: { defaultValue?: string }) => string,
): { label: string; className: string } {
  switch (type) {
    case 'daily':
      return {
        label: t('memory.nodeTypeDaily', { defaultValue: 'รายวัน' }),
        className: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
      };
    case 'periodic':
      return {
        label: t('memory.nodeTypePeriodic', { defaultValue: 'รายงวด' }),
        className: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
      };
    case 'station':
    case 'device':
      return {
        label: t('memory.nodeTypeHub', { defaultValue: 'ศูนย์กลาง' }),
        className: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
      };
    case 'topic':
      return {
        label: t('memory.nodeTypeTopic', { defaultValue: 'หัวข้อ' }),
        className: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
      };
    case 'hub':
      return {
        label: t('memory.nodeTypeHub', { defaultValue: 'Hub' }),
        className: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
      };
    case 'knowledge_document':
      return {
        label: t('memory.nodeTypeDocument', { defaultValue: 'เอกสาร' }),
        className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
      };
    default:
      return { label: type, className: 'bg-app-muted text-app-text-sec' };
  }
}

function graphNodeFromDaily(item: DailySummaryItem): GraphNode {
  const scope = scopeForNodeType('daily');
  return {
    id: `daily-${item.summary_date}-${item.org_id}`,
    type: 'daily',
    label: item.summary_date,
    radius: 8,
    color: SCOPE_LEVEL_COLORS[scope],
    scope,
    data: item,
  };
}

function graphNodeFromPeriodic(item: PeriodicSummaryItem): GraphNode {
  const scope = scopeForNodeType('periodic');
  return {
    id: `periodic-${item.period_type}-${item.period_start_date}-${item.org_id}`,
    type: 'periodic',
    label: `${item.period_type} · ${item.period_start_date}`,
    radius: 8,
    color: SCOPE_LEVEL_COLORS[scope],
    scope,
    data: item,
  };
}

function GraphSelectionAside({
  node,
  onClear,
  className = '',
  dailyItems = [],
  periodicItems = [],
  onSelectNode,
}: {
  node: GraphNode;
  onClear: () => void;
  className?: string;
  dailyItems?: DailySummaryItem[];
  periodicItems?: PeriodicSummaryItem[];
  onSelectNode?: (node: GraphNode) => void;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const badge = nodeTypeBadge(node.type, t);

  const docItem = node.type === 'knowledge_document' ? (node.data as KnowledgeDocumentItem) : null;
  const { content, loading: contentLoading, fetch: fetchContent } = useKnowledgeDocumentContent(
    docItem?.id || '',
  );

  useEffect(() => {
    if (node.type === 'knowledge_document' && docItem?.id && !content && !contentLoading) {
      void fetchContent();
    }
  }, [node.type, docItem?.id, content, contentLoading, fetchContent]);

  const relatedDaily = useMemo(() => {
    if (node.type === 'topic') {
      return dailyItems.filter((d) => summaryMatchesTopic(d.summary_md, node.label));
    }
    return [];
  }, [node, dailyItems]);

  const relatedPeriodic = useMemo(() => {
    if (node.type === 'topic') {
      return periodicItems.filter((p) => summaryMatchesTopic(p.summary_md, node.label));
    }
    return [];
  }, [node, periodicItems]);

  return (
    <aside
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-app-border/70 bg-app-surface/97 shadow-2xl ring-1 ring-app-accent/15 backdrop-blur-lg transition-[left,width,max-width] duration-200 ${className}`}
      style={isExpanded ? { left: '0.5rem', width: 'auto', maxWidth: 'none', minWidth: '0' } : undefined}
      role="dialog"
      aria-label={t('memory.selectionDetail', { defaultValue: 'รายละเอียดที่เลือก' })}
    >
      <header className="shrink-0 border-b border-app-border/60 bg-gradient-to-r from-app-accent/12 via-app-muted/30 to-transparent px-4 py-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
              {t('memory.selectionDetail', { defaultValue: 'รายละเอียดที่เลือก' })}
            </p>
            <p className="mt-1 break-words text-[19px] font-bold leading-tight text-app-text">
              {node.label}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-app-border/80 bg-app-surface text-app-text-sec transition-colors hover:border-app-accent/50 hover:bg-app-accent/10 hover:text-app-accent"
              aria-label={isExpanded ? t('memory.collapsePanel', { defaultValue: 'ย่อหน้าต่าง' }) : t('memory.expandPanel', { defaultValue: 'ขยายหน้าต่าง' })}
            >
              {isExpanded ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={onClear}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-app-border/80 bg-app-surface text-app-text-sec transition-colors hover:border-app-accent/50 hover:bg-app-accent/10 hover:text-app-accent"
              aria-label={t('memory.clearSelection', { defaultValue: 'ปิดรายละเอียด' })}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <span className={`rounded-md px-2.5 py-1 text-[12px] font-semibold ${badge.className}`}>
            {badge.label}
          </span>
          {node.data && (node.type === 'daily' || node.type === 'periodic') ? (
            <StatusBadge variant="info" className="!px-2.5 !py-0.5 !text-[12px]">
              {(node.data as DailySummaryItem | PeriodicSummaryItem).source_turn_count}{' '}
              {t('memory.turns', { defaultValue: 'turns' })}
            </StatusBadge>
          ) : null}
          {node.type === 'knowledge_document' && node.data ? (
            <StatusBadge variant="info" className="!px-2.5 !py-0.5 !text-[12px]">
              {formatBytes((node.data as KnowledgeDocumentItem).size_bytes)}
            </StatusBadge>
          ) : null}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {node.type === 'station' || node.type === 'device' ? (
          <p className="mb-4 text-[15px] leading-relaxed text-app-text-sec">
            {node.type === 'station'
              ? t('memory.stationHub', { defaultValue: 'ศูนย์กลางความจำสถานี' })
              : t('memory.deviceHub', { defaultValue: 'ศูนย์กลางความจำอุปกรณ์' })}
          </p>
        ) : null}

        {node.type === 'topic' ? (
          <p className="mb-4 text-[15px] leading-relaxed text-app-text-sec">
            {t('memory.topicNode', {
              defaultValue: 'หัวข้อความสำคัญที่เชื่อมสรุปหลายวันเข้าด้วยกัน',
            })}
          </p>
        ) : null}

        <div className="mb-4">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
            {t('memory.scopeLegend.nodeDetail', { defaultValue: 'Scopes ที่เกี่ยวข้อง' })}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {allScopesForNode(node.type, node.scope).map((scopeId) => (
              <span
                key={scopeId}
                className="inline-flex items-center gap-1.5 rounded-lg border border-app-border/70 bg-app-muted/30 px-2.5 py-1.5 text-[13px] font-medium text-app-text"
              >
                <span
                  className="h-2.5 w-2.5 rounded-full ring-1 ring-white/20"
                  style={{ backgroundColor: SCOPE_LEVEL_COLORS[scopeId] }}
                />
                {t(`memory.scopeLevel.${scopeId}.name`, { defaultValue: scopeId })}
              </span>
            ))}
          </div>
        </div>

        {node.data && (node.type === 'daily' || node.type === 'periodic') ? (
          <div className="rounded-lg border border-app-border/60 bg-app-muted/15 p-4 text-[15px] leading-relaxed text-app-text-sec prose-sm max-w-none">
            <MarkdownText>
              {(node.data as DailySummaryItem | PeriodicSummaryItem).summary_md}
            </MarkdownText>
          </div>
        ) : null}

        {node.type === 'knowledge_document' ? (
          <div className="space-y-3">
            {!node.data ? (
              <p className="text-[15px] text-app-text-dim">
                {t('knowledge.noData', { defaultValue: 'ไม่มีข้อมูลเอกสาร' })}
              </p>
            ) : (
              <>
                <div className="rounded-lg border border-app-border/60 bg-app-muted/15 p-4 space-y-3.5">
                  <div>
                    <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
                      {t('knowledge.filename', { defaultValue: 'ชื่อไฟล์' })}
                    </p>
                    <p className="mt-1 text-[15px] text-app-text break-words font-mono">
                      {(node.data as KnowledgeDocumentItem).filename}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
                        {t('knowledge.mimeType', { defaultValue: 'ชนิดไฟล์' })}
                      </p>
                      <p className="mt-1 text-[15px] text-app-text-sec break-words">
                        {(node.data as KnowledgeDocumentItem).mime_type}
                      </p>
                    </div>
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
                        {t('knowledge.size', { defaultValue: 'ขนาด' })}
                      </p>
                      <p className="mt-1 text-[15px] text-app-text-sec">
                        {formatBytes((node.data as KnowledgeDocumentItem).size_bytes)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
                      {t('knowledge.statusLabel', { defaultValue: 'สถานะ' })}
                    </p>
                    <StatusBadge variant={statusVariantForKnowledge((node.data as KnowledgeDocumentItem).status)} className="!px-2.5 !py-1 !text-[13px] mt-1.5">
                      {t(`knowledge.status.${(node.data as KnowledgeDocumentItem).status}`, {
                        defaultValue: (node.data as KnowledgeDocumentItem).status,
                      })}
                    </StatusBadge>
                  </div>
                  {(node.data as KnowledgeDocumentItem).error && (
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-wide text-red-600">
                        {t('knowledge.error', { defaultValue: 'ข้อผิดพลาด' })}
                      </p>
                      <p className="mt-1 text-[15px] text-red-600 leading-snug">
                        {(node.data as KnowledgeDocumentItem).error}
                      </p>
                    </div>
                  )}
                </div>

                {content || (node.data as KnowledgeDocumentItem).content ? (
                  <div>
                    <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim mb-2">
                      {t('knowledge.content', { defaultValue: 'เนื้อหา' })}
                    </p>
                    <div className="rounded-lg border border-app-border/60 bg-app-muted/15 p-4 max-h-64 overflow-y-auto text-[15px] leading-relaxed text-app-text-sec prose-sm prose-compact">
                      {contentLoading ? (
                        <p className="text-app-text-dim">
                          {t('knowledge.loading', { defaultValue: 'กำลังโหลดเอกสาร…' })}
                        </p>
                      ) : (
                        <MarkdownText>
                          {content || (node.data as KnowledgeDocumentItem).content!}
                        </MarkdownText>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-[15px] leading-relaxed text-app-text-dim">
                    {t('knowledge.documentInfo', {
                      defaultValue:
                        'เอกสารนี้ถูกอัปโหลดเข้าระบบ RAG เพื่อเป็นข้อมูลอ้างอิงของ Agent Station',
                    })}
                  </p>
                )}
              </>
            )}
          </div>
        ) : null}

        {node.type === 'topic' &&
        (relatedDaily.length > 0 || relatedPeriodic.length > 0) ? (
          <div className="space-y-3">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-app-text-dim">
              {t('memory.relatedSummaries', { defaultValue: 'สรุปที่เกี่ยวข้อง' })}
            </p>
            {relatedDaily.length > 0 ? (
              <ul className="space-y-2">
                {relatedDaily.slice(0, 8).map((item) => {
                  const dailyNode = graphNodeFromDaily(item);
                  return (
                    <li key={`${item.summary_date}-${item.org_id}`}>
                      <button
                        type="button"
                        className="w-full rounded-lg border border-app-border/60 bg-app-muted/20 px-3 py-2.5 text-left transition-colors hover:border-app-accent/40 hover:bg-app-accent/5"
                        onClick={() => onSelectNode?.(dailyNode)}
                      >
                        <span className="text-[15px] font-semibold text-app-text">
                          {item.summary_date}
                        </span>
                        <span className="ml-2 text-[13px] text-app-text-dim">
                          {item.source_turn_count} {t('memory.turns', { defaultValue: 'turns' })}
                        </span>
                        <p className="mt-1 line-clamp-2 text-[14px] leading-snug text-app-text-dim">
                          {item.summary_md.replace(/<!--[\s\S]*?-->/g, '').slice(0, 120)}…
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {relatedPeriodic.length > 0 ? (
              <ul className="space-y-2">
                {relatedPeriodic.slice(0, 4).map((item) => {
                  const periodicNode = graphNodeFromPeriodic(item);
                  return (
                    <li key={`${item.period_type}-${item.period_start_date}`}>
                      <button
                        type="button"
                        className="w-full rounded-lg border border-app-border/60 bg-app-muted/20 px-3 py-2.5 text-left transition-colors hover:border-app-accent/40 hover:bg-app-accent/5"
                        onClick={() => onSelectNode?.(periodicNode)}
                      >
                        <span className="text-[15px] font-semibold text-app-text">
                          {item.period_type} · {item.period_start_date}
                        </span>
                        <span className="ml-2 text-[13px] text-app-text-dim">
                          {item.source_turn_count} {t('memory.turns', { defaultValue: 'turns' })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        ) : null}

        {node.type === 'topic' &&
        relatedDaily.length === 0 &&
        relatedPeriodic.length === 0 ? (
          <p className="text-[15px] leading-relaxed text-app-text-dim">
            {t('memory.topicNoSummaries', {
              defaultValue: 'ยังไม่พบสรุปที่มีหัวข้อนี้ในช่วงที่โหลด',
            })}
          </p>
        ) : null}
      </div>
    </aside>
  );
}

/*  Periodic ring graph  */

const PERIOD_PALETTE = [
  'rgb(var(--color-app-accent))',
  'rgb(var(--color-app-success))',
  'rgb(var(--color-app-warning))',
  'rgb(var(--color-app-master))',
];

function PeriodDonut({ items }: { items: PeriodicSummaryItem[] }) {
  const data = useMemo(() => {
    const counts: Record<string, number> = { week: 0, month: 0, quarter: 0, year: 0 };
    items.forEach((it) => {
      counts[it.period_type] = (counts[it.period_type] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  }, [items]);

  if (!data.length) return null;
  const total = data.reduce((s, e) => s + e.value, 0);

  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-20 relative">
        <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={22} outerRadius={36} paddingAngle={2}>
              {data.map((_, i) => (
                <Cell key={i} fill={PERIOD_PALETTE[i % PERIOD_PALETTE.length]} stroke="none" />
              ))}
            </Pie>
            <RTooltip
              contentStyle={{
                background: 'rgb(var(--color-app-surface))',
                border: '1px solid rgb(var(--color-app-border))',
                borderRadius: 8,
                fontSize: 11,
                padding: '4px 8px',
              }}
              formatter={(value) => [`${Number(value || 0)} records`, '']}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-xs font-semibold text-app-text tabular-nums">{total}</span>
        </div>
      </div>
      <div className="space-y-1">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center gap-2 text-[11px]">
            <span
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: PERIOD_PALETTE[i % PERIOD_PALETTE.length] }}
            />
            <span className="text-app-text-sec">{d.name}</span>
            <span className="text-app-text-dim tabular-nums">·</span>
            <span className="text-app-text font-medium tabular-nums">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/*  Skeleton (loading)  */

function SkeletonCard() {
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5 animate-pulse">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 w-32 bg-app-muted rounded" />
          <div className="h-2.5 w-48 bg-app-muted/70 rounded" />
        </div>
        <div className="h-6 w-20 bg-app-muted rounded-md" />
      </div>
      <div className="space-y-2">
        <div className="h-2.5 w-full bg-app-muted/60 rounded" />
        <div className="h-2.5 w-11/12 bg-app-muted/60 rounded" />
        <div className="h-2.5 w-4/5 bg-app-muted/60 rounded" />
      </div>
    </div>
  );
}

/* Event graph (D3) */

function nodeFillOpacity(inv: ScopeInvolvement, filtering: boolean): number {
  if (!filtering) return 0.88;
  switch (inv) {
    case 'primary':
      return 0.95;
    case 'secondary':
      return 0.42;
    default:
      return 0.08;
  }
}

function nodeStrokeOpacity(inv: ScopeInvolvement, filtering: boolean): number {
  if (!filtering) return 0.08;
  switch (inv) {
    case 'primary':
      return 0.45;
    case 'secondary':
      return 0.28;
    default:
      return 0.05;
  }
}

type NodeLabelStyle = {
  fill: string;
  stroke: string;
  strokeWidth: number;
  fontSize: string;
  fontWeight: number;
  opacity: number;
};

/** Label on filled nodes - primary uses high-contrast text when region filter is active. */
function nodeLabelStyle(inv: ScopeInvolvement, filtering: boolean): NodeLabelStyle {
  if (!filtering) {
    return {
      fill: 'currentColor',
      stroke: 'rgba(10, 11, 13, 0.45)',
      strokeWidth: 2,
      fontSize: '10px',
      fontWeight: 500,
      opacity: 0.55,
    };
  }
  switch (inv) {
    case 'primary':
      return {
        fill: '#ffffff',
        stroke: 'rgba(10, 11, 13, 0.65)',
        strokeWidth: 3,
        fontSize: '11px',
        fontWeight: 700,
        opacity: 1,
      };
    case 'secondary':
      return {
        fill: '#f8fafc',
        stroke: 'rgba(10, 11, 13, 0.5)',
        strokeWidth: 2,
        fontSize: '10px',
        fontWeight: 600,
        opacity: 0.92,
      };
    default:
      return {
        fill: 'currentColor',
        stroke: 'none',
        strokeWidth: 0,
        fontSize: '9px',
        fontWeight: 400,
        opacity: 0.22,
      };
  }
}

function linkStrokeOpacity(inv: ScopeInvolvement, filtering: boolean): number {
  if (!filtering) return 0.12;
  return inv === 'primary' ? 0.7 : 0.06;
}

/** Update graph colors/opacity when legend scope/node filter changes (no simulation restart). */
function applyGraphRegionHighlight(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  nodes: GraphNode[],
  links: GraphLink[],
  activeHighlightId: string | null,
  regionHighlight: Map<string, ScopeInvolvement>,
  centerNodeId: string | null,
): void {
  const filtering = Boolean(activeHighlightId);
  const nodeInvolvement = (d: GraphNode) => regionHighlight.get(d.id) ?? 'none';
  svg
    .selectAll<SVGLineElement, GraphLink>('line.graph-link')
    .attr('stroke', (d) => {
      const scopes = scopesForLinkType(d.type);
      return SCOPE_LEVEL_COLORS[scopes[0]];
    })
    .attr('stroke-opacity', (d) => {
      const scope = scopesForLinkType(d.type)[0];
      return linkStrokeOpacity(linkInvolvementForScope(d.type, filtering ? scope : null), filtering);
    })
    .attr('stroke-width', (d) => {
      if (!filtering) return d.type === 'temporal' ? 1.5 : 1;
      return 1;
    });

  svg.selectAll<SVGGElement, GraphNode>('g.node').each(function (d) {
    const inv = nodeInvolvement(d);
    const isHub = d.id === centerNodeId;
    const group = d3.select(this);

    group
      .select('circle.node-halo')
      .attr('stroke-opacity', isHub ? 0.5 : filtering ? 0.2 : 0.5)
      .attr('fill-opacity', isHub ? 0.12 : 0);

    group
      .select('circle.node-ring')
      .attr('stroke', d.color)
      .attr('stroke-opacity', isHub ? 0.6 : nodeStrokeOpacity(inv, filtering))
      .attr('stroke-width', () => {
        if (isHub) return 2;
        if (!filtering) return 1.5;
        return inv === 'primary' ? 2.5 : inv === 'secondary' ? 2 : 1;
      });

    group
      .select('circle.node-fill')
      .attr('fill', d.color)
      .attr('fill-opacity', isHub
        ? filtering && inv === 'none'
          ? 0.22
          : 0.95
        : nodeFillOpacity(inv, filtering))
      .attr('stroke', () =>
        isHub
          ? HUB_GLOW
          : filtering && inv === 'primary'
            ? d.color
            : d.color,
      )
      .attr('stroke-width', isHub ? 2.5 : filtering && inv === 'primary' ? 2.5 : inv === 'secondary' ? 2 : 1);

    group.select('text').each(function () {
      const el = d3.select(this);
      if (isHub) {
        el.attr('opacity', filtering && inv === 'none' ? 0.35 : 1);
        return;
      }
      const style = nodeLabelStyle(inv, filtering);
      el.attr('font-size', style.fontSize)
        .attr('font-weight', style.fontWeight)
        .attr('fill', style.fill)
        .attr('stroke', style.stroke)
        .attr('stroke-width', style.strokeWidth)
        .attr('opacity', style.opacity);
    });
  });

}

function nodePhaseSeed(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) % 9973;
  return h;
}

/**
 * Continuously injects gentle, per-node time-based velocity so the spring
 * simulation never settles. Because it feeds the same velocity field that the
 * link force reads, connected nodes visibly tug each other (real spring feel).
 */
function makeWanderForce(
  centerNodeId: string | null,
  draggingIds: Set<string>,
  amp: number,
): d3.Force<GraphNode, undefined> {
  let simNodes: GraphNode[] = [];
  const force: d3.Force<GraphNode, undefined> = () => {
    const t = performance.now() / 1000;
    for (const n of simNodes) {
      if (centerNodeId && n.id === centerNodeId) continue;
      if (draggingIds.has(n.id)) continue;
      const seed = nodePhaseSeed(n.id);
      const fx = 0.22 + (seed % 53) / 120; // ~0.22-0.66
      const fy = 0.18 + (seed % 41) / 130;
      n.vx = (n.vx ?? 0) + Math.cos(t * fx + seed) * amp;
      n.vy = (n.vy ?? 0) + Math.sin(t * fy + seed * 0.7) * amp;
    }
  };
  force.initialize = (n: GraphNode[]) => {
    simNodes = n;
  };
  return force;
}

type GraphLayoutMetrics = {
  cx: number;
  cy: number;
  minDim: number;
  rx: number;
  ry: number;
};

const RING_RX_FACTOR: Record<MemoryGraphNodeType, number> = {
  daily: 0.68,
  periodic: 0.84,
  topic: 0.96,
  station: 0.35,
  device: 0.35,
  hub: 0.42,
  knowledge_document: 0.55,
};

const RING_RY_FACTOR: Record<MemoryGraphNodeType, number> = {
  daily: 0.72,
  periodic: 0.86,
  topic: 0.98,
  station: 0.38,
  device: 0.38,
  hub: 0.45,
  knowledge_document: 0.58,
};

function computeGraphLayoutMetrics(width: number, height: number): GraphLayoutMetrics {
  const pad = 32;
  const cx = width / 2;
  const cy = height / 2;
  return {
    cx,
    cy,
    minDim: Math.min(width, height),
    rx: Math.max(56, width / 2 - pad),
    ry: Math.max(52, height / 2 - pad),
  };
}

/** Elliptical rings - uses full width/height of the graph panel. */
function seedOrbitLayout(
  nodes: GraphNode[],
  centerNodeId: string | null,
  metrics: GraphLayoutMetrics,
): void {
  const { cx, cy } = metrics;
  const hub = centerNodeId ? nodes.find((n) => n.id === centerNodeId) : null;
  if (hub) {
    hub.x = cx;
    hub.y = cy;
  }

  const buckets: Record<'daily' | 'periodic' | 'topic' | 'other', GraphNode[]> = {
    daily: [],
    periodic: [],
    topic: [],
    other: [],
  };

  for (const node of nodes) {
    if (centerNodeId && node.id === centerNodeId) continue;
    if (node.type === 'daily') buckets.daily.push(node);
    else if (node.type === 'periodic') buckets.periodic.push(node);
    else if (node.type === 'topic') buckets.topic.push(node);
    else buckets.other.push(node);
  }

  buckets.daily.sort((a, b) => a.label.localeCompare(b.label));

  const placeOnRing = (list: GraphNode[], ringOffset = 0) => {
    const count = list.length;
    if (count === 0) return;
    const sample = list[0];
    const layers =
      sample.type === 'daily' && count > 12 ? Math.min(3, Math.ceil(count / 10)) : 1;
    const start = -Math.PI / 2 + ringOffset;
    list.forEach((node, i) => {
      const layer = layers > 1 ? i % layers : 0;
      const layerScale = layers > 1 ? 0.82 + (layer / Math.max(layers - 1, 1)) * 0.2 : 1;
      const angle = start + ((Math.PI * 2) * i) / count;
      const rx = metrics.rx * (RING_RX_FACTOR[node.type] ?? 0.65) * layerScale;
      const ry = metrics.ry * (RING_RY_FACTOR[node.type] ?? 0.65) * layerScale;
      node.x = cx + rx * Math.cos(angle);
      node.y = cy + ry * Math.sin(angle);
    });
  };

  placeOnRing(buckets.daily, 0);
  placeOnRing(buckets.periodic, Math.PI / 9);
  placeOnRing(buckets.topic, Math.PI / 5);
  placeOnRing(buckets.other, Math.PI / 7);

  if (!centerNodeId) {
    nodes.forEach((node, i) => {
      const angle = ((Math.PI * 2) * i) / Math.max(nodes.length, 1);
      node.x = cx + metrics.rx * 0.55 * Math.cos(angle);
      node.y = cy + metrics.ry * 0.55 * Math.sin(angle);
    });
  }
}

function linkDistance(link: GraphLink, metrics: GraphLayoutMetrics): number {
  const base = (metrics.rx + metrics.ry) / 14;
  switch (link.type) {
    case 'temporal':
      return base * 4.2;
    case 'ownership':
      return base * 5;
    default:
      return base * 5;
  }
}

function linkStrength(link: GraphLink): number {
  switch (link.type) {
    case 'ownership':
      return 0.04;
    case 'temporal':
      return 0.08;
    default:
      return 0.12;
  }
}

function fitGraphToView(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<Element, unknown>,
  nodes: GraphNode[],
  width: number,
  height: number,
): void {
  if (nodes.length === 0) return;
  const pad = 40;
  const xs = nodes.map((n) => n.x ?? 0);
  const ys = nodes.map((n) => n.y ?? 0);
  const minX = Math.min(...xs) - pad;
  const maxX = Math.max(...xs) + pad;
  const minY = Math.min(...ys) - pad;
  const maxY = Math.max(...ys) + pad;
  const bw = Math.max(maxX - minX, 1);
  const bh = Math.max(maxY - minY, 1);
  const scale = Math.min(width / bw, height / bh, 1.15);
  const tx = width / 2 - scale * (minX + maxX) / 2;
  const ty = height / 2 - scale * (minY + maxY) / 2;
  const transform = d3.zoomIdentity.translate(tx, ty).scale(scale);
  svg.call(zoom.transform as any, transform);
}

function MemoryGraph({
  dailyItems,
  periodicItems,
  filterOrg,
  graphFocus = null,
  highlightStation = null,
  highlightDevice = null,
  onNodeClick,
  knowledgeDocuments = [],
}: {
  dailyItems: DailySummaryItem[];
  periodicItems: PeriodicSummaryItem[];
  filterOrg: string;
  graphFocus?: MemoryGraphFocus | null;
  highlightStation?: string | null;
  highlightDevice?: string | null;
  onNodeClick?: (node: GraphNode) => void;
  knowledgeDocuments?: KnowledgeDocumentItem[];
}) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const [graphSize, setGraphSize] = useState({ width: 800, height: 400 });

  const { nodes, links, centerNodeId } = useMemo(() => {
    const capped = capItemsForGraph(dailyItems, periodicItems);
    return buildGraphData(
      capped.daily,
      capped.periodic,
      filterOrg,
      graphFocus,
      knowledgeDocuments,
    );
  }, [dailyItems, periodicItems, filterOrg, graphFocus, knowledgeDocuments]);

  const activeHighlightId = highlightStation ?? highlightDevice ?? null;

  const regionHighlight = useMemo(() => {
    const linkInputs = links.map((l) => ({
      source: typeof l.source === 'string' ? l.source : String((l.source as GraphNode).id),
      target: typeof l.target === 'string' ? l.target : String((l.target as GraphNode).id),
      type: l.type,
    }));
    return computeGraphNodeHighlight(
      nodes.map((n) => ({ id: n.id, type: n.type, scope: n.scope })),
      linkInputs,
      activeHighlightId,
    );
  }, [nodes, links, activeHighlightId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const width = Math.max(200, el.clientWidth);
      const height = Math.max(240, el.clientHeight);
      setGraphSize({ width, height });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;

    const { width, height } = graphSize;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const layout = computeGraphLayoutMetrics(width, height);
    const { cx, cy } = layout;

    seedOrbitLayout(nodes, centerNodeId, layout);

    const floatingHubLayout = Boolean(centerNodeId);

    nodes.forEach((node) => {
      node.anchorX = node.x ?? cx;
      node.anchorY = node.y ?? cy;
      node.vx = 0;
      node.vy = 0;
    });

    if (centerNodeId) {
      const hub = nodes.find((n) => n.id === centerNodeId);
      if (hub) {
        hub.fx = cx;
        hub.fy = cy;
        hub.x = cx;
        hub.y = cy;
        hub.anchorX = cx;
        hub.anchorY = cy;
      }
    }
    const filtering = Boolean(activeHighlightId);
    const nodeInvolvement = (d: GraphNode) => regionHighlight.get(d.id) ?? 'none';
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const linkEndpoint = (end: string | GraphNode): GraphNode | undefined =>
      typeof end === 'string' ? nodeById.get(end) : end;

    const g = svg.append('g');

    const linkElements = g
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('class', 'graph-link')
      .attr('stroke', (d: GraphLink) => {
        const scopes = scopesForLinkType(d.type);
        return SCOPE_LEVEL_COLORS[scopes[0]];
      })
      .attr('stroke-opacity', (d: GraphLink) =>
        linkStrokeOpacity(linkInvolvementForScope(d.type, null), filtering),
      )
      .attr('stroke-width', (d: GraphLink) => {
        return d.type === 'temporal' ? 1.5 : 1;
      })
      .attr('stroke-dasharray', (d: GraphLink) => {
        if (d.type === 'ownership') return '2,2';
        return 'none';
      });

    const nodeGroups = g
      .selectAll('g.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .style('cursor', (d: GraphNode) => (isGraphNodeClickable(d.type) ? 'pointer' : 'default'))
      .on('click', (_e, d: GraphNode) => {
        if (isGraphNodeClickable(d.type) && onNodeClick) {
          onNodeClick(d);
        }
      });

    // Hub: soft glow halo so the center clearly outranks every other node.
    nodeGroups
      .filter((d: GraphNode) => d.id === centerNodeId)
      .append('circle')
      .attr('class', 'node-halo')
      .attr('r', (d: GraphNode) => d.radius + 12)
      .attr('fill', HUB_GLOW)
      .attr('fill-opacity', 0.12)
      .attr('stroke', HUB_GLOW)
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 2);

    nodeGroups
      .append('circle')
      .attr('class', 'node-ring')
      .attr('r', (d: GraphNode) => d.radius + 3)
      .attr('fill', 'none')
      .attr('stroke', (d: GraphNode) => d.color)
      .attr('stroke-opacity', (d: GraphNode) =>
        d.id === centerNodeId ? 0.6 : nodeStrokeOpacity(nodeInvolvement(d), filtering),
      )
      .attr('stroke-width', (d: GraphNode) => {
        if (d.id === centerNodeId) return 2;
        const inv = nodeInvolvement(d);
        if (!filtering) return 1.5;
        return inv === 'primary' ? 2.5 : inv === 'secondary' ? 2 : 1;
      });

    nodeGroups
      .append('circle')
      .attr('class', 'node-fill')
      .attr('r', (d: GraphNode) => d.radius)
      .attr('fill', (d: GraphNode) => d.color)
      .attr('fill-opacity', (d: GraphNode) =>
        d.id === centerNodeId ? 0.95 : nodeFillOpacity(nodeInvolvement(d), filtering),
      )
      .attr('stroke', (d: GraphNode) =>
        d.id === centerNodeId
          ? HUB_GLOW
          : d.color,
      )
      .attr('stroke-width', (d: GraphNode) => {
        if (d.id === centerNodeId) return 2.5;
        const inv = nodeInvolvement(d);
        if (!filtering) return 1.5;
        return inv === 'primary' ? 2.5 : inv === 'secondary' ? 2 : 1;
      });

    nodeGroups.append('title').text((d: GraphNode) => {
      const scopeName = t(`memory.scopeLevel.${d.scope}.name`, { defaultValue: d.scope });
      const role = t(`memory.scopeLevel.${d.scope}.role`, { defaultValue: '' });
      return `${d.label}\n${scopeName}${role ? `\n${role}` : ''}`;
    });

    nodeGroups
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('dy', 0)
      .attr('paint-order', 'stroke fill')
      .attr('pointer-events', 'none')
      .each(function (d: GraphNode) {
        const el = d3.select(this);
        if (d.id === centerNodeId) {
          el.attr('font-size', '12px')
            .attr('font-weight', 700)
            .attr('fill', '#06222a')
            .attr('stroke', HUB_GLOW)
            .attr('stroke-width', 2.5)
            .attr('opacity', 1);
          return;
        }
        const inv = nodeInvolvement(d);
        const style = nodeLabelStyle(inv, filtering);
        el.attr('font-size', style.fontSize)
          .attr('font-weight', style.fontWeight)
          .attr('fill', style.fill)
          .attr('stroke', style.stroke)
          .attr('stroke-width', style.strokeWidth)
          .attr('opacity', style.opacity);
      })
      .text((d: GraphNode) => {
        if (d.id === centerNodeId) return d.label;
        if (d.type === 'daily') return d.label.slice(-5);
        if (d.type === 'periodic') return d.label.slice(0, 3);
        if (d.type === 'device' || d.type === 'station') return d.label.slice(0, 12);
        return d.label.slice(0, 8);
      });

    const zoom = d3.zoom().on('zoom', (e: any) => {
      g.attr('transform', e.transform);
    });

    svg.call(zoom as any);

    svg.on('dblclick.zoom', null);

    const paint = () => {
      linkElements
        .attr('x1', (d: GraphLink) => linkEndpoint(d.source as string | GraphNode)?.x ?? 0)
        .attr('y1', (d: GraphLink) => linkEndpoint(d.source as string | GraphNode)?.y ?? 0)
        .attr('x2', (d: GraphLink) => linkEndpoint(d.target as string | GraphNode)?.x ?? 0)
        .attr('y2', (d: GraphLink) => linkEndpoint(d.target as string | GraphNode)?.y ?? 0);

      nodeGroups.attr('transform', (d: GraphNode) => `translate(${d.x ?? 0},${d.y ?? 0})`);
    };

    if (!floatingHubLayout) {
      // Free force layout (no hub) - keep the classic d3 simulation.
      const drag = d3
        .drag<SVGGElement, GraphNode>()
        .on('start', (e) => {
          const sim = simulationRef.current;
          if (sim && !e.active) sim.alphaTarget(0.3).restart();
          e.subject.fx = e.subject.x;
          e.subject.fy = e.subject.y;
        })
        .on('drag', (e) => {
          e.subject.fx = e.x;
          e.subject.fy = e.y;
        })
        .on('end', (e) => {
          const sim = simulationRef.current;
          if (sim && !e.active) sim.alphaTarget(0);
          e.subject.fx = null;
          e.subject.fy = null;
        });
      nodeGroups.call(drag as any);

      const simulation = d3
        .forceSimulation(nodes)
        .velocityDecay(0.5)
        .alphaDecay(0.022)
        .force(
          'link',
          d3
            .forceLink<GraphNode, GraphLink>(links)
            .id((d) => d.id)
            .distance((l) => linkDistance(l, layout))
            .strength((l) => linkStrength(l)),
        )
        .force('charge', d3.forceManyBody().strength(-40).distanceMax(Math.max(layout.rx, layout.ry) * 0.9))
        .force(
          'collision',
          d3.forceCollide<GraphNode>().radius((d) => d.radius + 14).strength(0.92).iterations(5),
        )
        .force('center', d3.forceCenter(cx, cy).strength(0.06));

      simulationRef.current = simulation;
      simulation.on('tick', paint);
      simulation.alpha(1).restart();

      applyGraphRegionHighlight(svg, nodes, links, activeHighlightId, regionHighlight, centerNodeId);

      return () => {
        simulation.stop();
      };
    }

    // Hub layout: settle once, then keep a warm spring simulation running
    // Pre-settle so the initial frame is laid out, then never fully cool down
    // so link springs visibly transmit tension between connected nodes.
    const draggingIds = new Set<string>();

    const settle = d3
      .forceSimulation(nodes)
      .velocityDecay(0.5)
      .force(
        'link',
        d3
          .forceLink<GraphNode, GraphLink>(links)
          .id((d) => d.id)
          .distance((l) => linkDistance(l, layout))
          .strength((l) => Math.min(0.6, linkStrength(l) * 4)),
      )
      .force('charge', d3.forceManyBody().strength(-22).distanceMax(Math.max(layout.rx, layout.ry) * 0.9))
      .force(
        'collision',
        d3.forceCollide<GraphNode>().radius((d) => d.radius + 12).strength(0.95).iterations(4),
      )
      .stop();

    for (let i = 0; i < 180; i += 1) settle.tick();

    nodes.forEach((node) => {
      node.anchorX = node.id === centerNodeId ? cx : node.x ?? cx;
      node.anchorY = node.id === centerNodeId ? cy : node.y ?? cy;
    });

    paint();
    fitGraphToView(svg, zoom, nodes, width, height);

    const wanderAmp = Math.min(0.18, Math.max(0.07, (layout.rx + layout.ry) / 5200));
    const wander = makeWanderForce(centerNodeId, draggingIds, wanderAmp);

    const simulation = d3
      .forceSimulation(nodes)
      .velocityDecay(0.4) // very soft: heavy damping so links barely pull
      .alphaDecay(0) // never cool down
      .alphaTarget(0.08) // stay barely warm
      .force(
        'link',
        d3
          .forceLink<GraphNode, GraphLink>(links)
          .id((d) => d.id)
          .distance((l) => linkDistance(l, layout))
          .strength((l) => Math.min(0.08, linkStrength(l) * 0.6)),
      )
      .force('charge', d3.forceManyBody().strength(-18).distanceMax(Math.max(layout.rx, layout.ry) * 0.9))
      .force(
        'collision',
        d3.forceCollide<GraphNode>().radius((d) => d.radius + 9).strength(0.85).iterations(3),
      )
      .force('x', d3.forceX<GraphNode>((d) => d.anchorX ?? cx).strength(0.022))
      .force('y', d3.forceY<GraphNode>((d) => d.anchorY ?? cy).strength(0.022))
      .force('wander', wander);

    simulationRef.current = simulation;

    if (centerNodeId) {
      const hub = nodes.find((n) => n.id === centerNodeId);
      if (hub) {
        hub.fx = cx;
        hub.fy = cy;
      }
    }

    const drag = d3
      .drag<SVGGElement, GraphNode>()
      .on('start', (e) => {
        const d = e.subject;
        draggingIds.add(d.id);
        if (!e.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (e) => {
        const d = e.subject;
        d.fx = e.x;
        d.fy = e.y;
      })
      .on('end', (e) => {
        const d = e.subject;
        draggingIds.delete(d.id);
        if (!e.active) simulation.alphaTarget(0.12);
        if (d.id === centerNodeId) {
          // Hub stays where it was dropped.
          d.fx = d.x;
          d.fy = d.y;
        } else {
          // Let other nodes spring back into the constellation.
          d.fx = null;
          d.fy = null;
        }
      });

    nodeGroups.call(drag as any);

    simulation.on('tick', paint);
    simulation.alpha(0.6).restart();

    applyGraphRegionHighlight(svg, nodes, links, activeHighlightId, regionHighlight, centerNodeId);

    return () => {
      simulation.stop();
    };
  // activeHighlightId/regionHighlight intentionally excluded: a dedicated useEffect below
  // handles highlight-only updates so the full D3 simulation is not rebuilt on each change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, links, onNodeClick, graphSize, centerNodeId, t]);

  useEffect(() => {
    if (!svgRef.current || nodes.length === 0) return;
    const svg = d3.select(svgRef.current);
    applyGraphRegionHighlight(svg, nodes, links, activeHighlightId, regionHighlight, centerNodeId);
  }, [nodes, links, activeHighlightId, regionHighlight, centerNodeId]);

  return (
    <div ref={containerRef} className="h-full w-full min-h-0">
      <svg
        ref={svgRef}
        width={graphSize.width}
        height={graphSize.height}
        className="block max-h-full max-w-full"
      />
    </div>
  );
}

/*  Main component  */

const REFRESH_OPTIONS = [
  { label: '30s', ms: 30_000 },
  { label: '1m', ms: 60_000 },
  { label: '5m', ms: 300_000 },
];

const RECENT_LIST_LIMIT = 6;
const GRAPH_MAX_PERIODIC = 8;
const GRAPH_MAX_TOPICS = 22;

/** Keep the force graph readable - cap periodic rows and low-value topic hubs. */
function capItemsForGraph(
  dailyItems: DailySummaryItem[],
  periodicItems: PeriodicSummaryItem[],
): { daily: DailySummaryItem[]; periodic: PeriodicSummaryItem[] } {
  const daily = [...dailyItems].sort((a, b) => b.summary_date.localeCompare(a.summary_date)).slice(0, 45);
  const periodicSorted = [...periodicItems].sort((a, b) =>
    b.period_end_date.localeCompare(a.period_end_date),
  );
  const seenPeriodic = new Set<string>();
  const periodic: PeriodicSummaryItem[] = [];
  for (const row of periodicSorted) {
    const key = `${row.period_type}|${row.period_start_date}|${row.org_id}`;
    if (seenPeriodic.has(key)) continue;
    seenPeriodic.add(key);
    periodic.push(row);
    if (periodic.length >= GRAPH_MAX_PERIODIC) break;
  }
  return { daily, periodic };
}

type StationMemorySummaryPanelProps = {
  deviceId?: string;
  agentId?: string;
  embedded?: boolean;
  /** Card embed: stats + filters only (no graph / recent lists). */
  compact?: boolean;
  /** Station-level panel on agent-management (hide device scope). */
  stationScope?: boolean;
  /** Increment to trigger a knowledge document refresh (e.g. after upload). */
  knowledgeRefetchKey?: number;
  /**
   * Device ID to use exclusively for the knowledge documents query.
   * Overrides scopedDeviceId for useHippocampusKnowledge without affecting
   * the daily/periodic memory data scope (which still follows deviceId).
   */
  knowledgeDeviceId?: string;
};

export default function StationMemorySummaryPanel({
  deviceId,
  agentId,
  embedded = false,
  compact = false,
  stationScope = false,
  knowledgeRefetchKey,
  knowledgeDeviceId,
}: StationMemorySummaryPanelProps) {
  const { t } = useTranslation();
  const { orgId, stationId: contextStationId } = useAgentOrgContext();
  const { stations, activeStationId, setActiveStationId, loading: stationsLoading } = useStation();
  const [searchQuery, setSearchQuery] = useState('');
  const [periodType, setPeriodType] = useState<'' | 'week' | 'month' | 'quarter' | 'year'>('');
  const pageSize = 30;

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshIntervalMs, setRefreshIntervalMs] = useState(30_000);
  const [selectedGraphNode, setSelectedGraphNode] = useState<GraphNode | null>(null);
  const [historyTab, setHistoryTab] = useState<'daily' | 'periodic'>('daily');
  const [highlightStation, setHighlightStation] = useState<string | null>(null);
  const [highlightDevice, setHighlightDevice] = useState<string | null>(null);

  const stationId = activeStationId;
  const orgIdResolved = orgId || contextStationId;
  const scopedDeviceId = deviceId?.trim() ?? '';

  const stationLatest = useMemorySummaryLatest(orgIdResolved, stationId);
  const stationDaily = useMemorySummaryDaily(orgIdResolved, stationId, 30, 0, pageSize);
  const stationPeriodic = useMemorySummaryPeriodic(periodType, orgIdResolved, stationId, 0, pageSize);
  const deviceMemory = useDeviceMemory(orgIdResolved, stationId, scopedDeviceId, agentId);
  const knowledgeQueryDeviceId =
    knowledgeDeviceId !== undefined ? knowledgeDeviceId || null : scopedDeviceId || null;
  const hippocampusKnowledge = useHippocampusKnowledge(
    orgIdResolved,
    stationId,
    knowledgeQueryDeviceId,
    // Always include station-shared docs: on a device they appear as inherited
    // nodes; with no device the backend returns station docs regardless.
    true,
  );

  // Refresh knowledge docs when parent signals a new upload (e.g. from KnowledgePanel).
  useEffect(() => {
    if (knowledgeRefetchKey === undefined) return;
    void hippocampusKnowledge.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeRefetchKey]);

  // neocortex_summaries rollup - direct from memory-service v2 (bypasses chat-api proxy)
  const neocortexRollup = useNeocortexRollup(orgIdResolved, stationId);

  const latest = scopedDeviceId ? deviceMemory.latest : stationLatest;
  const daily = scopedDeviceId ? deviceMemory.daily : stationDaily;
  const periodic = scopedDeviceId ? deviceMemory.periodic : stationPeriodic;

  const knowledgeDocuments: KnowledgeDocumentItem[] = useMemo(
    () => hippocampusKnowledge.data?.documents ?? [],
    [hippocampusKnowledge.data?.documents],
  );
  const knowledgeReadyCount =
    hippocampusKnowledge.data?.counts_by_status?.ready ?? 0;
  const knowledgeProcessingCount =
    (hippocampusKnowledge.data?.counts_by_status?.processing ?? 0) +
    (hippocampusKnowledge.data?.counts_by_status?.uploaded ?? 0);
  const knowledgeFailedCount =
    hippocampusKnowledge.data?.counts_by_status?.failed ?? 0;
  const knowledgeChunkCount = hippocampusKnowledge.data?.chunk_count ?? 0;

  /* Auto refresh */
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      void latest.refresh();
      void daily.refresh();
      void periodic.refresh();
      void hippocampusKnowledge.refresh();
      void neocortexRollup.refresh();
    }, refreshIntervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshIntervalMs, latest.refresh, daily.refresh, periodic.refresh, hippocampusKnowledge.refresh, neocortexRollup.refresh]);

  const handleManualRefresh = () => {
    void latest.refresh();
    void daily.refresh();
    void periodic.refresh();
    void hippocampusKnowledge.refresh();
    void neocortexRollup.refresh();
  };

  /*  Data filters (client-side)  */
  const filteredDailyItems = useMemo(() => {
    if (!searchQuery.trim()) return daily.data?.items ?? [];
    const q = searchQuery.toLowerCase();
    return (daily.data?.items ?? []).filter(
      (i) =>
        i.summary_md.toLowerCase().includes(q) ||
        (i.summary_date && i.summary_date.toLowerCase().includes(q)) ||
        (i.station_id && i.station_id.toLowerCase().includes(q)),
    );
  }, [daily.data, searchQuery]);

  const filteredPeriodicItems = useMemo(() => {
    if (!searchQuery.trim()) return periodic.data?.items ?? [];
    const q = searchQuery.toLowerCase();
    return (periodic.data?.items ?? []).filter(
      (i) =>
        i.summary_md.toLowerCase().includes(q) ||
        (i.period_type && i.period_type.toLowerCase().includes(q)) ||
        (i.station_id && i.station_id.toLowerCase().includes(q)),
    );
  }, [periodic.data, searchQuery]);

  const recentDailyItems = useMemo(
    () =>
      [...filteredDailyItems]
        .sort((a, b) => b.summary_date.localeCompare(a.summary_date))
        .slice(0, RECENT_LIST_LIMIT),
    [filteredDailyItems],
  );

  const recentPeriodicItems = useMemo(
    () =>
      [...filteredPeriodicItems]
        .sort((a, b) => b.period_end_date.localeCompare(a.period_end_date))
        .slice(0, RECENT_LIST_LIMIT),
    [filteredPeriodicItems],
  );

  /** One row per period key (type + start + org); keep highest turn count when duplicates exist. */
  const recentPeriodicUnique = useMemo(() => {
    const byKey = new Map<string, PeriodicSummaryItem>();
    for (const item of recentPeriodicItems) {
      const key = `${item.period_type}-${item.period_start_date}-${item.org_id}`;
      const prev = byKey.get(key);
      if (!prev || (item.source_turn_count ?? 0) > (prev.source_turn_count ?? 0)) {
        byKey.set(key, item);
      }
    }
    return [...byKey.values()].sort((a, b) =>
      b.period_end_date.localeCompare(a.period_end_date),
    );
  }, [recentPeriodicItems]);

  /*  Export helpers (no toast)  */
  const exportDailyCSV = () => {
    if (!daily.data) return;
    exportCSV(
      [
        ['summary_date', 'org_id', 'station_id', 'source_turn_count', 'summary_md'],
        ...daily.data.items.map((i) => [
          i.summary_date,
          i.org_id ?? '',
          i.station_id ?? '',
          String(i.source_turn_count),
          i.summary_md,
        ]),
      ],
      `memory_daily_${stationId || 'all'}.csv`,
    );
    toast.success(`Exported ${daily.data.items.length} daily records`);
  };

  const handleExportJSON = (kind: 'daily' | 'periodic') => {
    if (kind === 'daily' && daily.data) {
      exportJSON(daily.data.items, `memory_daily_${stationId || 'all'}.json`);
      toast.success('Daily summaries exported as JSON');
    } else if (kind === 'periodic' && periodic.data) {
      exportJSON(periodic.data.items, `memory_periodic_${stationId || 'all'}.json`);
      toast.success('Periodic summaries exported as JSON');
    }
  };

  /*  Computed stats  */
  const dailyCount = daily.data?.total ?? 0;
  const periodicCount = periodic.data?.total ?? 0;
  const latestDailyDate = latest.data?.daily?.summary_date ?? '\u2014';
  const latestUpdatedAt = latest.data?.daily?.updated_at ?? latest.data?.daily?.created_at;

  const sortedDailyAsc = useMemo(
    () => [...(daily.data?.items ?? [])].sort((a, b) => a.summary_date.localeCompare(b.summary_date)),
    [daily.data],
  );

  const totalTurns30d = useMemo(
    () => sortedDailyAsc.reduce((sum, it) => sum + (it.source_turn_count || 0), 0),
    [sortedDailyAsc],
  );

  const dailyDelta = useMemo(() => {
    if (sortedDailyAsc.length < 14) return null;
    const last7 = sortedDailyAsc.slice(-7).reduce((s, i) => s + i.source_turn_count, 0);
    const prev7 = sortedDailyAsc.slice(-14, -7).reduce((s, i) => s + i.source_turn_count, 0);
    if (prev7 === 0) return null;
    const pct = Math.round(((last7 - prev7) / prev7) * 100);
    return { value: Math.abs(pct), positive: pct >= 0 };
  }, [sortedDailyAsc]);

  const graphFocus = useMemo((): MemoryGraphFocus | null => {
    if (scopedDeviceId) return { kind: 'device', deviceId: scopedDeviceId };
    if (stationId) return { kind: 'station', stationId };
    return null;
  }, [scopedDeviceId, stationId]);

  const graphScopeNodes = useMemo((): GraphScopeHighlightInput[] => {
    const items = daily.data?.items ?? [];
    const knDocs = knowledgeDocuments;
    if (!items.length && !knDocs.length) return [];
    const capped = capItemsForGraph(items, periodic.data?.items ?? []);
    const { nodes } = buildGraphData(
      capped.daily,
      capped.periodic,
      orgIdResolved,
      graphFocus,
      knDocs,
    );
    return nodes.map((n) => ({ id: n.id, type: n.type, scope: n.scope }));
  }, [daily.data?.items, periodic.data?.items, knowledgeDocuments, orgIdResolved, graphFocus]);

  const periodicBreakdownHint = useMemo(() => {
    if (!periodic.data?.items.length) return '';
    const counts: Record<string, number> = { week: 0, month: 0, quarter: 0, year: 0 };
    periodic.data.items.forEach((it) => {
      counts[it.period_type] = (counts[it.period_type] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k[0].toUpperCase()}${v}`)
      .join(' · ');
  }, [periodic.data]);

  const activeStation = stations.find((s) => s.id === activeStationId);
  const activeStationLabel = activeStation?.name || activeStationId || 'All stations';
  const panelTitle = scopedDeviceId
    ? t('memory.deviceTitle', { defaultValue: 'ความจำอุปกรณ์' })
    : stationScope
      ? t('memory.stationTitle', { defaultValue: 'ความจำสถานี' })
      : t('memory.pageTitle');
  const panelDescription = scopedDeviceId
    ? t('memory.deviceDescription', {
        defaultValue: 'สรุปความจำและกราฟเหตุการณ์ — {{device}} · {{station}}',
        device: scopedDeviceId,
        station: activeStationLabel,
      })
    : stationScope
      ? t('memory.stationDescription', {
          defaultValue: 'ความจำระดับสถานีสำหรับ Manager และเอเจนต์ — {{station}}',
          station: activeStationLabel,
        })
      : t('memory.pageDescription');

  const detailHref =
    scopedDeviceId && agentId
      ? `/agent-management/${encodeURIComponent(agentId)}`
      : null;

  const hasAnyError = latest.error || daily.error || periodic.error;

  const outerClass = embedded
    ? compact
      ? 'flex w-full flex-col gap-2 rounded-lg border border-app-border/80 bg-app-muted/20 p-3'
      : 'flex w-full flex-col gap-3 rounded-xl border border-app-border bg-app-surface/50 p-4'
    : 'mx-auto flex min-h-[520px] w-full max-w-screen-2xl flex-col gap-3';

  const titleClass = compact ? 'text-sm font-semibold text-app-text' : 'text-lg font-semibold text-app-text';

  return (
    <section className={outerClass}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={titleClass}>{panelTitle}</h2>
          <p className={`${compact ? 'mt-0.5 text-xs' : 'mt-1 text-sm'} text-app-text-sec flex flex-wrap items-center gap-2`}>
            <span>{panelDescription}</span>
            {!scopedDeviceId ? (
              <StatusBadge variant="info" dot>
                {activeStationLabel}
              </StatusBadge>
            ) : null}
          </p>
        </div>
        {compact && detailHref ? (
          <Link
            to={detailHref}
            className="text-xs font-medium text-app-accent hover:underline shrink-0"
          >
            {t('memory.viewFullKnowledge', { defaultValue: 'ดูความจำทั้งหมด →' })}
          </Link>
        ) : null}
        {!compact ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={exportDailyCSV}
              disabled={!daily.data?.items.length}
              className="inline-flex items-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-2.5 py-1.5 text-xs hover:border-app-accent/40 hover:text-app-accent disabled:opacity-40"
            >
              <DocumentArrowDownIcon className="w-3.5 h-3.5" />
              CSV
            </button>
            <button
              type="button"
              onClick={() => handleExportJSON('daily')}
              disabled={!daily.data?.items.length}
              className="inline-flex items-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-2.5 py-1.5 text-xs hover:border-app-accent/40 hover:text-app-accent disabled:opacity-40"
            >
              JSON
            </button>
          </div>
        ) : null}
      </div>

      <div className={`shrink-0 grid grid-cols-2 gap-2.5 ${compact ? '' : 'sm:grid-cols-4 lg:grid-cols-5'}`}>
        <StatChip
          icon={DocumentTextIcon}
          label={t('memory.kpiLatest', { defaultValue: 'ล่าสุด' })}
          value={latestDailyDate}
          hint={latestUpdatedAt ? formatRelativeTime(latestUpdatedAt) : '\u2014'}
          accent="accent"
        />
        <StatChip
          icon={CalendarDaysIcon}
          label={t('memory.kpiDaily', { defaultValue: 'รายวัน' })}
          value={dailyCount.toLocaleString()}
          hint={t('memory.kpiDailyHint', { defaultValue: '30 วันล่าสุด' })}
          delta={dailyDelta}
        />
        <StatChip
          icon={ClockIcon}
          label={t('memory.kpiPeriodic', { defaultValue: 'รายงวด' })}
          value={periodicCount.toLocaleString()}
          hint={periodicBreakdownHint || '\u2014'}
        />
        <StatChip
          icon={BoltIcon}
          label={t('memory.kpiTurns', { defaultValue: 'เทิร์น (30d)' })}
          value={totalTurns30d.toLocaleString()}
          hint={t('memory.kpiTurnsHint', { defaultValue: 'จาก source_turn_count' })}
        />
        <StatChip
          icon={DocumentArrowDownIcon}
          label={t('memory.kpiKnowledge', { defaultValue: 'เอกสาร' })}
          value={knowledgeReadyCount.toLocaleString()}
          hint={
            knowledgeFailedCount > 0
              ? t('memory.kpiKnowledgeHintFailed', {
                  defaultValue: 'ready ? failed {n}',
                  n: knowledgeFailedCount,
                })
              : knowledgeProcessingCount > 0
                ? t('memory.kpiKnowledgeHintProcessing', {
                    defaultValue: 'ready ? {n} processing',
                    n: knowledgeProcessingCount,
                  })
                : t('memory.kpiKnowledgeHint', {
                    defaultValue: 'ready ? {chunks} chunks',
                    chunks: knowledgeChunkCount,
                  })
          }
        />
      </div>

      {!compact ? (
      <div className="shrink-0 rounded-xl border border-app-border/80 bg-app-surface/90 p-3 shadow-sm backdrop-blur-md">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-end">
          <div className="flex flex-wrap gap-2 lg:col-span-4">
            <p className="mb-1 w-full text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
              {t('memory.filtersLabel', { defaultValue: 'ตัวกรอง' })}
            </p>
            {!embedded && !scopedDeviceId ? (
              <select
                value={activeStationId}
                onChange={(e) => setActiveStationId(e.target.value)}
                disabled={stationsLoading}
                className="min-w-[8rem] flex-1 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm transition-all focus:border-app-accent focus:ring-2 focus:ring-app-accent/40 disabled:opacity-50"
              >
                <option value="">{t('memory.allStations', { defaultValue: 'ทุกสถานี' })}</option>
                {stations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
            ) : null}
            <select
              value={periodType}
              onChange={(e) =>
                setPeriodType((e.target.value as '' | 'week' | 'month' | 'quarter' | 'year') || '')
              }
              className="min-w-[8rem] flex-1 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm transition-all focus:border-app-accent focus:ring-2 focus:ring-app-accent/40"
            >
              <option value="">{t('memory.allPeriods', { defaultValue: 'ทุกงวด' })}</option>
              <option value="week">{t('memory.periodWeek', { defaultValue: 'สัปดาห์' })}</option>
              <option value="month">{t('memory.periodMonth', { defaultValue: 'เดือน' })}</option>
              <option value="quarter">{t('memory.periodQuarter', { defaultValue: 'ไตรมาส' })}</option>
              <option value="year">{t('memory.periodYear', { defaultValue: 'ปี' })}</option>
            </select>
          </div>

          <div className="lg:col-span-5">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
              {t('memory.searchLabel', { defaultValue: 'ค้นหา' })}
            </p>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-dim" />
              <input
                type="search"
                placeholder={t('memory.searchPlaceholder', {
                  defaultValue: 'ค้นหาในสรุปความจำ…',
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-app-border bg-app-surface py-2 pl-9 pr-3 text-sm text-app-text placeholder:text-app-text-dim transition-all focus:border-app-accent focus:outline-none focus:ring-2 focus:ring-app-accent/40"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 lg:col-span-3 lg:justify-end">
            <p className="mb-1 w-full text-[10px] font-semibold uppercase tracking-wide text-app-text-dim lg:text-right">
              {t('memory.refreshLabel', { defaultValue: 'อัปเดต' })}
            </p>
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={latest.loading || daily.loading || periodic.loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-app-border px-3 py-2 text-sm text-app-text transition-colors hover:bg-app-muted hover:text-app-accent disabled:opacity-50"
              aria-label={t('memory.refresh', { defaultValue: 'รีเฟรช' })}
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${
                  latest.loading || daily.loading || periodic.loading ? 'animate-spin' : ''
                }`}
              />
              {t('memory.refresh', { defaultValue: 'รีเฟรช' })}
            </button>
            {autoRefresh ? (
              <select
                value={refreshIntervalMs}
                onChange={(e) => setRefreshIntervalMs(Number(e.target.value))}
                className="rounded-lg border border-app-border bg-app-surface px-2 py-2 text-sm focus:ring-2 focus:ring-app-accent/40"
              >
                {REFRESH_OPTIONS.map((o) => (
                  <option key={o.ms} value={o.ms}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : null}
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-all ${
                autoRefresh
                  ? 'border-app-accent/40 bg-app-accent/15 text-app-accent shadow-[0_0_0_3px_rgb(var(--color-app-accent)/0.1)]'
                  : 'border-app-border text-app-text-sec hover:text-app-text'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  autoRefresh ? 'animate-pulse bg-app-accent' : 'bg-app-text-dim'
                }`}
              />
              {autoRefresh
                ? t('memory.autoOn', { defaultValue: 'อัตโนมัติ เปิด' })
                : t('memory.autoOff', { defaultValue: 'อัตโนมัติ ปิด' })}
            </button>
          </div>
        </div>
      </div>
      ) : null}

      {hasAnyError && (
        <div className="shrink-0 rounded-lg border border-app-error/20 bg-app-error/10 px-3 py-2 text-xs text-app-error">
          {[latest.error, daily.error, periodic.error].filter(Boolean).join(' ? ')}
        </div>
      )}

      {compact ? null : (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {/* Graph - canvas + detail panel on selection */}
        <Panel
          title={t('memory.graphTitle', { defaultValue: 'กราฟความจำ' })}
          subtitle={t('memory.graphSubtitle', {
            defaultValue: 'คลิกโหนดเพื่อดูสรุป · คลิกส่วนสมองเพื่อไฮไลต์',
          })}
          icon={SparklesIcon}
          className="lg:col-span-12 h-[88vh] min-h-[560px] scroll-mt-4 border-app-accent/35 shadow-lg ring-1 ring-app-accent/15"
          bodyClassName="!p-0 flex flex-col min-h-0 bg-gradient-to-b from-app-muted/10 to-transparent"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <MemoryGraphScopeLegend
              nodes={graphScopeNodes}
              highlightStation={highlightStation}
              highlightDevice={highlightDevice}
              onSelectStation={setHighlightStation}
              onSelectDevice={setHighlightDevice}
              compact
            />
            <div className="relative min-h-0 flex-1 p-2">
              {daily.data?.items?.length || knowledgeDocuments.length ? (
                <MemoryGraph
                  dailyItems={daily.data?.items ?? []}
                  periodicItems={periodic.data?.items ?? []}
                  filterOrg={orgIdResolved}
                  graphFocus={graphFocus}
                  highlightStation={highlightStation}
                  highlightDevice={highlightDevice}
                  onNodeClick={setSelectedGraphNode}
                  knowledgeDocuments={knowledgeDocuments}
                />
              ) : (
                <div className="flex h-full min-h-[240px] items-center justify-center">
                  <EmptyState
                    text={t('memory.graphEmpty', { defaultValue: 'ยังไม่มีข้อมูลสำหรับกราฟ' })}
                    icon={SparklesIcon}
                  />
                </div>
              )}
              {!selectedGraphNode && daily.data?.items?.length ? (
                <div className="pointer-events-none absolute bottom-3 left-3 z-10 max-w-[min(100%,20rem)] rounded-lg border border-app-border/60 bg-app-surface/90 px-3 py-2 text-[11px] leading-snug text-app-text-sec shadow-md backdrop-blur-sm">
                  {t('memory.graphTapHint', {
                    defaultValue: 'คลิกโหนดในกราฟ หรือเลือกจากไทม์ไลน์ด้านล่าง',
                  })}
                </div>
              ) : null}
              {selectedGraphNode ? (
                <GraphSelectionAside
                  node={selectedGraphNode}
                  onClear={() => setSelectedGraphNode(null)}
                  dailyItems={daily.data?.items ?? []}
                  periodicItems={periodic.data?.items ?? []}
                  onSelectNode={setSelectedGraphNode}
                  className="pointer-events-auto absolute bottom-2 right-2 top-2 z-20 w-[min(100%,30rem)] min-w-[320px] max-w-[56%]"
                />
              ) : null}
            </div>
          </div>
        </Panel>

        {/* Overview - 3 insight columns */}
        <Panel
          title={t('memory.insightsTitle', { defaultValue: 'ภาพรวมและแนวโน้ม' })}
          subtitle={t('memory.insightsSubtitle', {
            defaultValue: 'กิจกรรม 30 วัน · สัดส่วนงวด · สรุปล่าสุด',
          })}
          icon={ChartBarIcon}
          className="lg:col-span-12 scroll-mt-4"
          bodyClassName="!p-0 !overflow-visible"
        >
          <div className="flex flex-col divide-y divide-app-border/50">
            {/* Row 1: Activity chart — full width */}
            <div className="p-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
                {t('memory.activity30d', { defaultValue: 'กิจกรรม 30 วัน' })}
              </p>
              {daily.data?.items?.length ? (
                <DailySummaryChart items={daily.data.items} />
              ) : (
                <p className="text-xs text-app-text-dim">
                  {t('memory.noActivity', { defaultValue: 'ยังไม่มีข้อมูล' })}
                </p>
              )}
            </div>
            {/* Row 2: Periodic + Summary side-by-side */}
            <div className="grid flex-1 grid-cols-1 divide-y divide-app-border/50 lg:grid-cols-5 lg:divide-x lg:divide-y-0">
              <div className="p-4 lg:col-span-2">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
                  {t('memory.periodicOverview', { defaultValue: 'รายงวด' })}
                </p>
                {periodic.data?.items?.length ? (
                  <PeriodDonut items={periodic.data.items} />
                ) : (
                  <p className="text-xs text-app-text-dim">—</p>
                )}
              </div>
              <div className="p-4 lg:col-span-3">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
                  {t('memory.latestDaily', { defaultValue: 'สรุปรายวันล่าสุด' })}
                </p>
                {latest.loading && <SkeletonCard />}
                {neocortexRollup.data?.station_summary ? (
                  (() => {
                    const ss = neocortexRollup.data!.station_summary!;
                    const dateLabel = ss.period_start
                      ? new Date(ss.period_start).toISOString().slice(0, 10)
                      : ss.updated_at
                        ? new Date(ss.updated_at).toISOString().slice(0, 10)
                        : '—';
                    return (
                      <div className="rounded-xl border border-app-accent/25 bg-gradient-to-br from-app-accent/8 to-transparent p-3 text-left">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="text-base font-bold tabular-nums text-app-text">
                            {dateLabel}
                          </span>
                          <StatusBadge variant="info" className="!px-2 !py-0.5 !text-[10px]">
                            {ss.source_count}{' '}
                            {t('memory.turns', { defaultValue: 'turns' })}
                          </StatusBadge>
                        </div>
                        <div className="max-h-24 overflow-y-auto text-[11px] leading-relaxed text-app-text-sec">
                          <MarkdownText>{ss.summary_md}</MarkdownText>
                        </div>
                        <p className="mt-2 flex items-center gap-1 text-[10px] font-medium text-app-accent">
                          {t('memory.fromNeocortex', { defaultValue: 'สรุปจาก neocortex_summaries (v2)' })}
                        </p>
                      </div>
                    );
                  })()
                ) : latest.data?.daily ? (
                  <button
                    type="button"
                    onClick={() => setSelectedGraphNode(graphNodeFromDaily(latest.data!.daily!))}
                    className="group w-full rounded-xl border border-app-accent/25 bg-gradient-to-br from-app-accent/8 to-transparent p-3 text-left transition-all hover:border-app-accent/45 hover:shadow-md"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="text-base font-bold tabular-nums text-app-text">
                        {latest.data.daily.summary_date}
                      </span>
                      <StatusBadge variant="info" className="!px-2 !py-0.5 !text-[10px]">
                        {latest.data.daily.source_turn_count}{' '}
                        {t('memory.turns', { defaultValue: 'turns' })}
                      </StatusBadge>
                    </div>
                    <div className="max-h-24 overflow-y-auto text-[11px] leading-relaxed text-app-text-sec">
                      <MarkdownText>{latest.data.daily.summary_md}</MarkdownText>
                    </div>
                    <p className="mt-2 flex items-center gap-1 text-[10px] font-medium text-app-accent">
                      {t('memory.openInDetail', { defaultValue: 'เปิดในแผงรายละเอียด' })}
                      <ChevronRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </p>
                  </button>
                ) : !latest.loading && !neocortexRollup.loading ? (
                  <p className="text-xs text-app-text-dim">—</p>
                ) : null}
              </div>
            </div>
          </div>
          {/* per-device breakdown from neocortex_summaries (v2) */}
          {neocortexRollup.data?.device_summaries?.length ? (
            <div className="border-t border-app-border/50 p-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-app-text-dim">
                {t('memory.deviceBreakdown', { defaultValue: 'สรุปต่ออุปกรณ์ (neocortex v2)' })}
              </p>
              <div className="flex flex-wrap gap-2">
                {neocortexRollup.data.device_summaries.map((ds) => {
                  const dateLabel = ds.period_start
                    ? new Date(ds.period_start).toISOString().slice(0, 10)
                    : ds.updated_at
                      ? new Date(ds.updated_at).toISOString().slice(0, 10)
                      : '—';
                  return (
                    <div
                      key={ds.id}
                      className="min-w-[200px] max-w-xs flex-1 rounded-xl border border-app-border/60 bg-app-muted/20 p-2.5"
                    >
                      <div className="mb-1 flex items-center justify-between gap-1.5">
                        <span className="truncate text-[10px] font-semibold text-app-text-sec">
                          {ds.device_id}
                        </span>
                        <StatusBadge variant="info" className="!px-1.5 !py-0 !text-[9px]">
                          {ds.source_count} {t('memory.turns', { defaultValue: 'turns' })}
                        </StatusBadge>
                      </div>
                      <p className="text-[10px] text-app-text-dim">{dateLabel}</p>
                      <div className="mt-1 max-h-16 overflow-y-auto text-[11px] leading-relaxed text-app-text-sec">
                        <MarkdownText>{ds.summary_md}</MarkdownText>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </Panel>

        {/* Reference docs - knowledge_documents (Hippocampus view) */}
        <Panel
          title={t('memory.knowledgeTitle', { defaultValue: 'เอกสารอ้างอิง' })}
          subtitle={t('memory.knowledgeSubtitle', {
            defaultValue: 'เอกสารที่อัปโหลดไว้ ใช้อ้างอิงตอบคำถาม (RAG)',
          })}
          icon={DocumentTextIcon}
          className="lg:col-span-12 min-h-[88vh] scroll-mt-4"
          headerAction={
            <div className="flex items-center gap-1.5 text-[10px] text-app-text-dim">
              <span>
                {t('memory.knowledgeSummary', {
                  defaultValue: '{ready} ready ? {chunks} chunks',
                  ready: knowledgeReadyCount,
                  chunks: knowledgeChunkCount,
                })}
              </span>
            </div>
          }
        >
          {hippocampusKnowledge.loading && !hippocampusKnowledge.data ? (
            <p className="p-4 text-center text-xs text-app-text-dim">
              {t('common.loading', { defaultValue: 'กำลังโหลด…' })}
            </p>
          ) : knowledgeDocuments.length === 0 ? (
            <p className="p-4 text-center text-xs text-app-text-dim">
              {t('memory.knowledgeEmpty', {
                defaultValue: 'ยังไม่มีเอกสารอัปโหลด',
              })}
            </p>
          ) : (
            <ul className="divide-y divide-app-border/40">
              {knowledgeDocuments.slice(0, 10).map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-start gap-3 px-3 py-2.5 text-sm hover:bg-app-muted/30"
                >
                  <DocumentTextIcon className="mt-0.5 h-4 w-4 shrink-0 text-app-accent" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-app-text">
                        {doc.filename}
                      </span>
                      <StatusBadge variant={statusVariantForKnowledge(doc.status)}>
                        {doc.status}
                      </StatusBadge>
                    </div>
                    <div className="mt-0.5 text-[11px] text-app-text-dim">
                      {doc.owner_type === 'station' ? 'station' : `device ${doc.device_id}`}
                      {' ? '}
                      {(doc.size_bytes / 1024).toFixed(1)} KB
                      {' ? '}
                      {doc.mime_type}
                      {doc.error ? ` ? error: ${doc.error}` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Timeline - daily / periodic */}
        <Panel
          title={t('memory.timelineTitle', { defaultValue: 'ไทม์ไลน์ความจำ' })}
          subtitle={t('memory.timelineSubtitle', {
            defaultValue: 'เลือกรายการเพื่อดูสรุปเต็มบนกราฟ',
          })}
          icon={QueueListIcon}
          className="lg:col-span-12 min-h-[88vh] scroll-mt-4"
          bodyClassName="!p-0"
          headerAction={
            <div
              className="flex rounded-lg border border-app-border/80 bg-app-muted/30 p-0.5"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={historyTab === 'daily'}
                onClick={() => setHistoryTab('daily')}
                className={`rounded-md px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                  historyTab === 'daily'
                    ? 'bg-app-surface text-app-accent shadow-sm'
                    : 'text-app-text-dim hover:text-app-text'
                }`}
              >
                {t('memory.tabDaily', { defaultValue: 'รายวัน' })} ({recentDailyItems.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={historyTab === 'periodic'}
                onClick={() => setHistoryTab('periodic')}
                className={`rounded-md px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                  historyTab === 'periodic'
                    ? 'bg-app-surface text-app-accent shadow-sm'
                    : 'text-app-text-dim hover:text-app-text'
                }`}
              >
                {t('memory.tabPeriodic', { defaultValue: 'รายงวด' })} ({recentPeriodicUnique.length})
              </button>
            </div>
          }
        >
          {historyTab === 'daily' ? (
            recentDailyItems.length ? (
              <ul className="divide-y divide-app-border/40">
                {recentDailyItems.map((item) => {
                  const node = graphNodeFromDaily(item);
                  return (
                    <MemoryTimelineRow
                      key={`${item.summary_date}-${item.org_id}-${item.station_id}`}
                      primary={item.summary_date}
                      turns={item.source_turn_count ?? 0}
                      preview={item.summary_md}
                      selected={selectedGraphNode?.id === node.id}
                      onClick={() => setSelectedGraphNode(node)}
                    />
                  );
                })}
              </ul>
            ) : (
              <p className="p-4 text-center text-xs text-app-text-dim">
                {t('memory.timelineEmptyDaily', { defaultValue: 'ยังไม่มีสรุปรายวัน' })}
              </p>
            )
          ) : recentPeriodicUnique.length ? (
            <ul className="divide-y divide-app-border/40">
              {recentPeriodicUnique.map((item) => {
                const node = graphNodeFromPeriodic(item);
                return (
                  <MemoryTimelineRow
                    key={`${item.period_type}-${item.period_start_date}-${item.org_id}-${item.station_id}`}
                    primary={item.period_start_date}
                    secondary={item.period_type}
                    turns={item.source_turn_count ?? 0}
                    preview={item.summary_md}
                    selected={selectedGraphNode?.id === node.id}
                    onClick={() => setSelectedGraphNode(node)}
                  />
                );
              })}
            </ul>
          ) : (
            <p className="p-4 text-center text-xs text-app-text-dim">
              {t('memory.timelineEmptyPeriodic', { defaultValue: 'ยังไม่มีสรุปรายงวด' })}
            </p>
          )}
        </Panel>
      </div>
      )}

    </section>
  );
}
